import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import type { AdminTab, Game } from '../types';
import { ConfirmDialog, GameCreateModal } from '../components';
import { AdminAccountsSection } from '../components/AdminAccountsSection';
import { AdminExportImportBar } from '../components/AdminExportImportBar';
import { LevelUpPointsCard } from '../components/LevelUpPointsCard';
import { SpellEffectCard } from '../components/SpellEffectCard';
import { RulesMarkdownEditor } from '../components/RulesMarkdownEditor';
import { NpcManagerSection } from '../components/NpcManagerSection';
import { ClassMark, Pictogram } from '../components/Pictogram';
import { PictogramPicker } from '../components/PictogramPicker';
import { DEFAULT_CLASS_ICON, isKitIcon } from '../constants/pictograms';
import { formatItemCost } from '../utils/itemStats';
import { errorMessage, apiRequest } from '../api/client';
import { ADMIN_TABS, isAdminTab, paths } from '../navigation/paths';

const ADMIN_TAB_LABELS: Record<AdminTab, string> = {
  games: '🎮 Spiele',
  players: '👥 Spieler',
  characters: '⚔️ Charaktere',
  classes: '🏷️ Klassen',
  items: '🗡️ Items',
  spells: '✨ Zauber',
  rules: '📜 Regeln',
  admins: '👑 Konten',
  npcs: '👾 Gegner',
};

export function AdminPanelPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [gameToDelete, setGameToDelete] = useState<Game | null>(null);
  const showGameModal = location.pathname === paths.masterGameNew;
  const {
    adminTab: tabFromContext,
    setAdminTab,
    handleLogout,
    games,
    players,
    characters,
    classes,
    items,
    spells,
    rules,
    fetchRules,
    handleCreateGame,
    fetchGameOverview,
    handleDeleteGame,
    handleAddPlayer,
    handleDeletePlayer,
    handleOpenCharacterModal,
    handleEditCharacter,
    handleDeleteCharacter,
    calculateDamage,
    handleOpenItemModal,
    handleEditItem,
    handleDeleteItem,
    handleOpenSpellModal,
    handleEditSpell,
    handleDeleteSpell,
    editingClassId,
    isAddingClass,
    setEditingClassId,
    setIsAddingClass,
    newClass,
    setNewClass,
    handleAddClass,
    handleEditClass,
    handleUpdateClass,
    handleDeleteClass,
    handleCancelClassEdit,
  } = useApp();

  const activeTab = isAdminTab(tabFromContext) ? tabFromContext : 'games';

  return (
    <div className="admin-panel-epic compact-admin">
      <nav className="sticky-nav">
        <div className="nav-brand">
          <span className="nav-icon">👑</span>
          <span className="nav-title">Spielleiter-Kontrolle</span>
        </div>
        <div className="nav-buttons">
          <button className="nav-btn player-btn" aria-label="Spieler" onClick={() => navigate(paths.play)}>
            <span className="btn-icon" aria-hidden>⚔️</span>
            <span className="btn-label">Spieler</span>
          </button>
          <button className="nav-btn admin-btn active" aria-label="Spielleiter" onClick={() => navigate(paths.masterGames)}>
            <span className="btn-icon" aria-hidden>👑</span>
            <span className="btn-label">Spielleiter</span>
          </button>
          <button className="nav-btn logout-btn" aria-label="Ausloggen" onClick={handleLogout}>
            <span className="btn-icon" aria-hidden>🚪</span>
            <span className="btn-label">Ausloggen</span>
          </button>
        </div>
      </nav>

      <div className="admin-content-epic">
        <nav className="admin-tab-bar" aria-label="Admin-Bereiche">
          {ADMIN_TABS.map(tab => (
            <button
              key={tab}
              type="button"
              className={`admin-tab ${activeTab === tab ? 'active' : ''}`}
              onClick={() => setAdminTab(tab)}
            >
              {ADMIN_TAB_LABELS[tab]}
            </button>
          ))}
        </nav>

        <div className="admin-content-section">
          {activeTab === 'games' && (
            <div className="data-section">
              <div className="section-header">
                <h2>Spiele</h2>
                <button className="medieval-button" onClick={() => navigate(paths.masterGameNew)}>
                  + Spiel erstellen
                </button>
              </div>
              <div className="data-grid">
                {games.map(game => (
                  <div key={game.id} className="data-card game compact-card">
                    <div className="compact-card-main">
                      <div className="compact-card-head">
                        <h3>{game.name}</h3>
                        <span className="compact-pill">{game.status}</span>
                      </div>
                      <p className="compact-line">
                        {game.max_players} Spieler{game.is_team_game ? ` · Team ${game.team_size}` : ''}
                      </p>
                    </div>
                    <div className="card-actions compact-actions">
                      <button
                        className="medieval-button overview-button"
                        onClick={() => {
                          fetchGameOverview(game.id);
                          navigate(paths.masterGame(game.id));
                        }}
                      >
                        Übersicht
                      </button>
                      <button className="delete-button" onClick={() => setGameToDelete(game)}>
                        Löschen
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'players' && (
            <div className="data-section">
              <div className="section-header">
                <h2>Spieler</h2>
                <button className="medieval-button" onClick={handleAddPlayer}>
                  + Spieler hinzufügen
                </button>
              </div>
              <div className="data-grid">
                {players.map(player => (
                  <div key={player.id} className="data-card compact-card">
                    <div className="compact-card-main">
                      <div className="compact-card-head">
                        <h3>{player.name}</h3>
                      </div>
                      <p className="compact-line">{player.email || 'Keine E-Mail'}</p>
                    </div>
                    <div className="card-actions compact-actions">
                      <button className="delete-button" onClick={() => handleDeletePlayer(player.id)}>
                        Löschen
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'characters' && (
            <div className="data-section">
              <div className="section-header">
                <h2>Charaktere</h2>
                <div className="section-header-actions">
                  <AdminExportImportBar />
                  <button className="medieval-button" onClick={handleOpenCharacterModal}>
                    + Charakter hinzufügen
                  </button>
                </div>
              </div>
              <p className="section-info">Nur Vorlagen. Katalog inkl. Items, Zauber und Gegner — Kopien gehören zum Spiel und verschwinden mit ihm.</p>
              <div className="data-grid">
                {characters.filter(c => !c.player_id && !c.base_character_id).map(character => {
                  const maxHp = character.health_formula ? calculateDamage(character.health_formula, character, '50+constitution*5') : (character.max_health || 100);
                  const maxMana = character.mana_formula ? calculateDamage(character.mana_formula, character, '20+intelligence*3') : (character.max_mana || 50);
                  const movement = calculateDamage(character.movement_formula, character, '3+dexterity*0.1');
                  return (
                  <div key={character.id} className="data-card character compact-card">
                    <div className="compact-card-main">
                      <div className="compact-card-head">
                        <h3>{character.name}</h3>
                        <span className="compact-pill">{character.class} · {character.level}</span>
                      </div>
                      <p className="compact-line">❤️ {character.health}/{maxHp} · ✨ {character.mana || 0}/{maxMana} · ➤ {movement}</p>
                      <p className="compact-line">
                        🎒 {(character.item_ids || []).map((id) => items.find((item) => item.id === id)?.name).filter(Boolean).join(', ') || 'keine Items'}
                        {' · '}
                        ✨ {(character.spell_ids || []).map((id) => spells.find((spell) => spell.id === id)?.name).filter(Boolean).join(', ') || 'keine Zauber'}
                      </p>
                      <div className="character-stats-preview">
                        <span title="Stärke">💪{character.strength || 10}</span>
                        <span title="Geschick">🎯{character.dexterity || 10}</span>
                        <span title="Intelligenz">🧠{character.intelligence || 10}</span>
                        <span title="Konstitution">❤️{character.constitution || 10}</span>
                      </div>
                    </div>
                    <div className="card-actions compact-actions">
                      <button className="medieval-button edit-btn" onClick={() => handleEditCharacter(character)}>
                        Bearbeiten
                      </button>
                      <button className="delete-button" onClick={() => handleDeleteCharacter(character.id)}>
                        Löschen
                      </button>
                    </div>
                  </div>
                );
                })}
              </div>
            </div>
          )}

          {activeTab === 'classes' && (
            <div className="data-section">
              <div className="section-header">
                <h2>Klassen</h2>
                {editingClassId === null && !isAddingClass && (
                  <button className="medieval-button" onClick={() => {
                    setEditingClassId(null);
                    setIsAddingClass(true);
                    setNewClass({ name: '', icon: DEFAULT_CLASS_ICON, order_index: classes.length });
                  }}>
                    + Klasse hinzufügen
                  </button>
                )}
              </div>
              <p className="section-info">Auswahl bei der Charaktererstellung.</p>

              {(editingClassId !== null || isAddingClass) && (
                <div className="rule-form-card">
                  <h3>{editingClassId ? 'Klasse bearbeiten' : 'Neue Klasse erstellen'}</h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '5px', color: '#ffd700' }}>Name *</label>
                      <input
                        type="text"
                        value={newClass.name}
                        onChange={e => setNewClass({ ...newClass, name: e.target.value })}
                        placeholder="z.B. Barde, Druide"
                        style={{
                          width: '100%', padding: '10px', background: 'rgba(0, 0, 0, 0.5)',
                          border: '1px solid #8b4513', borderRadius: '5px', color: '#fff'
                        }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '5px', color: '#ffd700' }}>Wappen</label>
                      <PictogramPicker
                        kind="class"
                        value={isKitIcon(newClass.icon) ? newClass.icon : ''}
                        onChange={icon => setNewClass({ ...newClass, icon: icon.src })}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '5px', color: '#ffd700' }}>Reihenfolge</label>
                      <input
                        type="number"
                        value={newClass.order_index}
                        onChange={e => setNewClass({ ...newClass, order_index: parseInt(e.target.value) || 0 })}
                        min={0}
                        style={{
                          width: '100px', padding: '10px', background: 'rgba(0, 0, 0, 0.5)',
                          border: '1px solid #8b4513', borderRadius: '5px', color: '#fff'
                        }}
                      />
                    </div>
                    <div style={{ display: 'flex', gap: '10px' }}>
                      <button
                        className="medieval-button"
                        onClick={editingClassId ? handleUpdateClass : handleAddClass}
                        disabled={!newClass.name.trim()}
                      >
                        {editingClassId ? '💾 Speichern' : '✨ Klasse erstellen'}
                      </button>
                      <button className="medieval-button cancel-btn" onClick={handleCancelClassEdit}>
                        Abbrechen
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <div className="data-grid">
                {classes
                  .filter(c => editingClassId === null || c.id !== editingClassId)
                  .map(cls => (
                    <div key={cls.id} className="data-card compact-card">
                      <div className="compact-card-main">
                        <div className="compact-card-head">
                          <ClassMark icon={cls.icon} klass={cls.name} className="compact-card-icon" />
                          <h3>{cls.name}</h3>
                        </div>
                      </div>
                      <div className="card-actions compact-actions">
                        <button className="medieval-button edit-btn" onClick={() => handleEditClass(cls)}>
                          Bearbeiten
                        </button>
                        <button className="delete-button" onClick={() => handleDeleteClass(cls.id)}>
                          Löschen
                        </button>
                      </div>
                    </div>
                  ))}
              </div>
              {classes.length === 0 && editingClassId === null && !isAddingClass && (
                <p style={{ textAlign: 'center', color: '#ccc', padding: '20px' }}>
                  Noch keine Klassen vorhanden. Erstelle die erste Klasse oder starte die API neu (Standard-Klassen werden automatisch angelegt).
                </p>
              )}
            </div>
          )}

          {activeTab === 'items' && (
            <div className="data-section">
              <div className="section-header">
                <h2>Items</h2>
                <div className="section-header-actions">
                  <AdminExportImportBar />
                  <button className="medieval-button" onClick={handleOpenItemModal}>
                    + Item hinzufügen
                  </button>
                </div>
              </div>
              <p className="section-info">Katalog exportieren/importieren (Charaktere, Items, Zauber, Gegner) — Spieler-Inventare bleiben erhalten.</p>
              <div className="data-grid">
                {items.map(item => {
                  const statBits = [
                    (item.strength_bonus || 0) !== 0 ? `💪 Stärke ${item.strength_bonus! > 0 ? '+' : ''}${item.strength_bonus}` : '',
                    (item.dexterity_bonus || 0) !== 0 ? `🎯 Geschick ${item.dexterity_bonus! > 0 ? '+' : ''}${item.dexterity_bonus}` : '',
                    (item.intelligence_bonus || 0) !== 0 ? `🧠 Intelligenz ${item.intelligence_bonus! > 0 ? '+' : ''}${item.intelligence_bonus}` : '',
                    (item.constitution_bonus || 0) !== 0 ? `❤️ Konstitution ${item.constitution_bonus! > 0 ? '+' : ''}${item.constitution_bonus}` : '',
                    (item.health_bonus || 0) !== 0 ? `💚 Leben ${item.health_bonus! > 0 ? '+' : ''}${item.health_bonus}` : '',
                    (item.damage_bonus || 0) !== 0 ? `⚔ Schaden ${item.damage_bonus! > 0 ? '+' : ''}${item.damage_bonus}` : '',
                  ].filter(Boolean);
                  const hasStatBonuses = statBits.length > 0;
                  return (
                    <div key={item.id} className="data-card item compact-card" title={[item.description, item.effect].filter(Boolean).join('\n')}>
                      <div className="compact-card-main">
                        <div className="compact-card-head">
                          <Pictogram iconUrl={item.icon_url} type={item.type} name={item.name} kind="item" className="compact-card-icon" />
                          <h3>{item.name}</h3>
                          <span className="compact-pill">{item.type}</span>
                        </div>
                        {formatItemCost(item) && <p className="compact-line">{formatItemCost(item)}</p>}
                        {item.effect && <p className="effect compact-line">{item.effect}</p>}
                        {hasStatBonuses && (
                          <div className="stat-bonus-list">
                            {statBits.map(bit => <span key={bit}>{bit}</span>)}
                          </div>
                        )}
                      </div>
                      <div className="card-actions compact-actions">
                        <button className="medieval-button edit-btn" onClick={() => handleEditItem(item)}>
                          Bearbeiten
                        </button>
                        <button className="delete-button" onClick={() => handleDeleteItem(item.id)}>
                          Löschen
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {activeTab === 'spells' && (
            <div className="data-section">
              <div className="section-header">
                <h2>Zauber</h2>
                <div className="section-header-actions">
                  <AdminExportImportBar />
                  <button className="medieval-button" onClick={handleOpenSpellModal}>
                    + Zauber hinzufügen
                  </button>
                </div>
              </div>
              <p className="section-info">Katalog exportieren/importieren (Charaktere, Items, Zauber, Gegner) — Spieler-Zauber bleiben erhalten.</p>
              <div className="data-grid">
                {spells.map(spell => (
                  <div key={spell.id} className="data-card spell compact-card" title={spell.description || undefined}>
                    <div className="compact-card-main">
                      <div className="compact-card-head">
                        <Pictogram iconUrl={spell.icon_url} name={spell.name} kind="spell" className="compact-card-icon" />
                        <h3>{spell.name}</h3>
                        <span className="compact-pill">Lvl {spell.level} · {spell.mana_cost} Mana</span>
                      </div>
                      {spell.effect && <p className="effect compact-line">{spell.effect}</p>}
                    </div>
                    <div className="card-actions compact-actions">
                      <button className="medieval-button edit-btn" onClick={() => handleEditSpell(spell)}>
                        Bearbeiten
                      </button>
                      <button className="delete-button" onClick={() => handleDeleteSpell(spell.id)}>
                        Löschen
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'rules' && (
            <div className="data-section">
              <div className="section-header">
                <h2>Regeln</h2>
              </div>

              <LevelUpPointsCard />
              <SpellEffectCard />
              <RulesMarkdownEditor
                rules={rules}
                onSave={async markdown => {
                  try {
                    await apiRequest('/api/rules/document', {
                      method: 'PUT',
                      auth: true,
                      body: { markdown },
                    });
                    await fetchRules();
                  } catch (err) {
                    throw new Error(errorMessage(err, 'Regeln konnten nicht gespeichert werden.'));
                  }
                }}
              />
            </div>
          )}

          {activeTab === 'admins' && (
            <AdminAccountsSection />
          )}

          {activeTab === 'npcs' && (
            <NpcManagerSection />
          )}
        </div>
      </div>

      {showGameModal && (
        <GameCreateModal
          onClose={() => navigate(paths.masterGames)}
          onSubmit={async (payload) => {
            await handleCreateGame(payload);
          }}
        />
      )}
      {gameToDelete && (
        <ConfirmDialog
          title="Spiel löschen?"
          message={`„${gameToDelete.name}“ wirklich löschen? Zugehörige Zuordnungen gehen verloren.`}
          confirmLabel="Löschen"
          danger
          onCancel={() => setGameToDelete(null)}
          onConfirm={() => {
            void handleDeleteGame(gameToDelete.id);
            setGameToDelete(null);
          }}
        />
      )}
    </div>
  );
}
